import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { Alert, Stack, styled, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { filter } from 'es-toolkit/compat';
import React, { ReactElement, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { customerBucketAccessGrantsQueryKey, getCustomerBucketAccessGrants } from 'api/customerBucketAccessGrants';
import { CollapsibleSection } from 'components/atoms/CollapsibleSection';
import { OsTabs } from 'components/atoms/OsTabs';
import { StudyCustomerBucketAccessGrantsControl } from 'components/StudiesDashboard/StudyCustomerBucketAccessGrants/StudyCustomerBucketAccessGrantsControl';
import { Permission } from 'interfaces/permissionOption';
import { getAwsTemporaryCredentials } from 'services/S3Utils';
import { detectBrowserOperatingSystem } from 'utils/osDetection';
import { useLabs } from 'utils/queryHooks/useLabs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';
import {
  AWS_PROFILE_NAME_ERROR,
  AwsProfileSelectionStep,
  isAwsProfileChoiceReady,
  isValidAwsProfileName,
} from './AwsProfileSelectionStep';
import { AWS_PROFILE_NAME } from './cliInstructionItNotes';
import CLIInstructions from './CLIInstructions';
import { OperatingSystem } from './cliInstructionTypes';
import CustomerGrantValidationStep from './CustomerGrantValidationStep';
import GrantBasedAwsUploadCommandGenerator from './GrantBasedAwsUploadCommandGenerator';
import LocalAwsUploadCommandGenerator from './LocalAwsUploadCommandGenerator';
import { StepHelp } from './StepHelp';
import TemporaryAwsCredentialsStep from './TemporaryAwsCredentialsStep';
import { UploadBox } from './UploadSlidesFormUI';

const MainBox = styled(UploadBox)(() => ({
  flex: 1,
  minHeight: 0,
  marginBottom: 0,
  gap: 10,
  padding: '0 10px 0px',
  marginTop: '20px',
}));

const getSourcePathInstructions = (selectedOs: OperatingSystem) => {
  if (selectedOs === 'windows') {
    return (
      <Typography variant="body2" color="text.secondary">
        On Windows, select the file or folder in File Explorer, right-click it, and choose <strong>Copy as path</strong>{' '}
        from the menu.
      </Typography>
    );
  }

  if (selectedOs === 'linux') {
    return (
      <Typography variant="body2" color="text.secondary">
        On Linux, copy the file or folder path from your file manager&apos;s location bar, or drag the file or folder
        into the terminal to insert its path.
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      On macOS, select the file or folder in Finder and press <strong>Option + Command + C</strong> to copy its path.
    </Typography>
  );
};

interface Props {
  studyId: string;
}

const AwsProfileNameField = ({
  awsProfileName,
  credentialMode,
  setAwsProfileName,
}: {
  awsProfileName: string;
  credentialMode: 'temporary-nucleai' | 'customer-aws';
  setAwsProfileName: (awsProfileName: string) => void;
}) => (
  <TextField
    label="AWS profile name"
    value={awsProfileName}
    onChange={(event) => setAwsProfileName(event.target.value)}
    fullWidth
    placeholder={credentialMode === 'temporary-nucleai' ? AWS_PROFILE_NAME : 'my-upload-profile'}
    error={Boolean(awsProfileName.trim()) && !isValidAwsProfileName(awsProfileName)}
    helperText={
      Boolean(awsProfileName.trim()) && !isValidAwsProfileName(awsProfileName)
        ? AWS_PROFILE_NAME_ERROR
        : credentialMode === 'temporary-nucleai'
        ? 'Use one profile name for the configure and upload commands. This defaults to a dedicated Nucleai upload profile.'
        : 'Leave blank to use your default AWS credentials. Enter a name only if you normally use --profile.'
    }
    sx={{ maxWidth: 420 }}
  />
);

const useStudySlideDirectory = (studyId: string) => {
  const { labId } = useCurrentLabId();
  const { data: labs, isLoading: isLoadingLabs } = useLabs();
  const customerBucketName = labs?.find((lab) => lab.id === labId)?.customerBucketName;
  const resolvedBucketName = customerBucketName || `nuc-cst-us-east-2-${labId}`;
  const slideDirectory = isLoadingLabs ? null : `s3://${resolvedBucketName}/ATOM/${studyId}/Incoming/`;

  return { isLoadingLabs, slideDirectory };
};

const GenerateCredentials = (props: Props): ReactElement => {
  const { studyId } = props;
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [awsProfileName, setAwsProfileName] = useState(AWS_PROFILE_NAME);

  const defaultOperatingSystem = detectBrowserOperatingSystem();
  const [selectedOsState, setSelectedOs] = useState<OperatingSystem | undefined>(undefined);
  const selectedOs = selectedOsState || defaultOperatingSystem;
  const [credentialsKeys, setCredentialsKeys] = useState({
    AccessKeyId: '',
    SecretAccessKey: '',
  });

  const [duringGenerate, setDuringGenerate] = useState(false);
  const [isGenerateFailed, setIsGenerateFailed] = useState(false);

  const { isLoadingLabs, slideDirectory } = useStudySlideDirectory(studyId);
  const region = 'us-east-2';

  const generateCredentials = async () => {
    if (isLoadingLabs) {
      return;
    }

    setIsGenerateFailed(false);
    setDuringGenerate(true);
    try {
      const requestedLabId = searchParams.get('labId');
      if (!requestedLabId) {
        setIsGenerateFailed(true);
        return;
      }

      const { Credentials } = await getAwsTemporaryCredentials(requestedLabId);
      setCredentialsKeys(Credentials);
    } catch {
      setIsGenerateFailed(true);
    } finally {
      setDuringGenerate(false);
    }
  };

  const exportExcelFile = () => {
    const data = [
      {
        AccessKeyId: credentialsKeys.AccessKeyId,
        SecretAccessKey: credentialsKeys.SecretAccessKey,
        Region: region,
      },
    ];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'User Credentials');
    XLSX.writeFile(workbook, 'user-credentials.xlsx');
  };

  return (
    <>
      <MainBox data-testid="nucleai-storage-container">
        {slideDirectory ? (
          <CLIInstructions
            connectStepContent={
              <Stack spacing={1.5}>
                <CollapsibleSection title="Upload profile">
                  <AwsProfileNameField
                    awsProfileName={awsProfileName}
                    credentialMode="temporary-nucleai"
                    setAwsProfileName={setAwsProfileName}
                  />
                </CollapsibleSection>
                <TemporaryAwsCredentialsStep
                  awsProfileName={awsProfileName}
                  credentials={credentialsKeys}
                  isError={isGenerateFailed}
                  isGenerating={duringGenerate}
                  isLoading={isLoadingLabs}
                  onExport={exportExcelFile}
                  onGenerate={generateCredentials}
                  region={region}
                />
              </Stack>
            }
            selectedOs={selectedOs}
            setSelectedOs={setSelectedOs}
            title="Upload slide from this computer"
            studyId={studyId}
            uploadStepContent={
              <LocalAwsUploadCommandGenerator
                awsProfileName={awsProfileName.trim()}
                destination={slideDirectory}
                selectedOs={selectedOs}
                sourcePathInstructions={
                  <CollapsibleSection title="Find the file or folder path">
                    <Stack spacing={1.5}>
                      <OsTabs
                        selectedOs={selectedOs}
                        setSelectedOs={setSelectedOs}
                        ariaLabel="Source path instructions by operating system"
                      />
                      {getSourcePathInstructions(selectedOs)}
                    </Stack>
                  </CollapsibleSection>
                }
              />
            }
            uploadStepTitle="Upload slides"
          />
        ) : (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Loading lab storage instructions...
            </Typography>
          </Box>
        )}
      </MainBox>
    </>
  );
};

export const CustomerBucketGrantUploadInstructions = ({ studyId }: { studyId: string }) => {
  const { hasPermission } = usePermissions();
  const { slideDirectory } = useStudySlideDirectory(studyId);
  const [awsProfileName, setAwsProfileName] = useState('');
  const [profileChoice, setProfileChoice] = useState<'default' | 'named' | null>(null);
  const [selectedOs, setSelectedOs] = useState<OperatingSystem>(detectBrowserOperatingSystem);
  const profileChoiceReady = isAwsProfileChoiceReady(profileChoice, awsProfileName);
  const canManageGrants =
    hasPermission(Permission.EditLabCustomerBucketAccessGrants) ||
    hasPermission(Permission.CreateOwnCustomerBucketAccessGrants);
  const grantsQuery = useQuery({
    queryKey: customerBucketAccessGrantsQueryKey(studyId),
    queryFn: ({ signal }) => getCustomerBucketAccessGrants(studyId, signal),
    enabled: Boolean(studyId) && canManageGrants,
  });
  const activeGrantCount = filter(grantsQuery.data || [], (grant) => grant.status === 'active').length;

  return (
    <MainBox data-testid="customer-bucket-grants-container">
      <CLIInstructions
        profileStep={{
          title: 'Choose your AWS profile',
          icon: <TerminalOutlinedIcon color="primary" fontSize="small" />,
          isReady: profileChoiceReady,
          continueBlockedReason:
            profileChoice === 'named' && awsProfileName.trim() && !isValidAwsProfileName(awsProfileName)
              ? AWS_PROFILE_NAME_ERROR
              : profileChoice === 'named'
              ? 'Enter the AWS profile name from the list before continuing.'
              : 'Choose the AWS profile you will use before continuing.',
          content: (
            <AwsProfileSelectionStep
              awsProfileName={awsProfileName}
              profileChoice={profileChoice}
              selectedOs={selectedOs}
              setAwsProfileName={setAwsProfileName}
              setProfileChoice={setProfileChoice}
              setSelectedOs={setSelectedOs}
            />
          ),
        }}
        configureStep={{
          title: 'Configure AWS upload access',
          icon: <VpnKeyOutlinedIcon color="primary" fontSize="small" />,
          isReady: activeGrantCount > 0,
          continueBlockedReason: grantsQuery.isLoading
            ? 'Loading AWS upload access…'
            : 'Configure AWS upload access before continuing.',
          content: (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Paste the role or user identifier below, then add access.
                {awsProfileName.trim()
                  ? ` The commands use profile ${awsProfileName.trim()}.`
                  : ' The commands use your default AWS credentials.'}
              </Typography>
              <StepHelp
                items={[
                  {
                    question: 'What do I paste?',
                    answer:
                      'The full Amazon identifier for the role or user. It starts with arn:aws:iam::. An email or display name is not enough.',
                  },
                  {
                    question: 'The command says the session expired.',
                    answer: 'Sign in again, then run the command again.',
                  },
                  {
                    question: 'It says access denied.',
                    answer: 'Ask your AWS administrator for the role or user identifier.',
                  },
                ]}
              />
              {activeGrantCount > 0 ? (
                <Alert severity="success">This role is saved. Continue to start uploading, or add another role.</Alert>
              ) : null}
              <StudyCustomerBucketAccessGrantsControl
                awsProfileName={awsProfileName}
                selectedOs={selectedOs}
                setSelectedOs={setSelectedOs}
                studyId={studyId}
              />
            </Stack>
          ),
        }}
        credentialMode="customer-aws"
        itNotesDestination={slideDirectory || ''}
        readyToUpload={activeGrantCount > 0}
        selectedOs={selectedOs}
        setSelectedOs={setSelectedOs}
        title="Upload slide from your organization's Amazon S3 bucket"
        studyId={studyId}
        uploadStepContent={
          <GrantBasedAwsUploadCommandGenerator
            awsProfileName={awsProfileName.trim()}
            operatingSystem={selectedOs}
            slideDirectory={slideDirectory || ''}
          />
        }
        uploadStepTitle="Upload slides"
        validateStep={{
          title: 'Validate AWS upload permissions',
          icon: <SecurityOutlinedIcon color="primary" fontSize="small" />,
          content: <CustomerGrantValidationStep studyId={studyId} />,
        }}
      />
    </MainBox>
  );
};

export default GenerateCredentials;
